import * as rrweb from 'rrweb';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { humanId } from 'human-id';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SessionableOptions, SessionMetrics, SessionPayload } from '../types';
import { listenerHandler } from '@rrweb/types';

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
  private options: SessionableOptions & {
    apiKey: string;
    autoStart: boolean;
    metadata: Record<string, any>;
    maskInputs: boolean;
    maskTextSelector?: string;
    debug: boolean;
    useDeviceId: boolean;
  };
  private anonymousUserId: string;
  private events: rrweb.eventWithTime[] = [];
  private stopFn: listenerHandler | null = null;
  private eventsInterval: ReturnType<typeof setInterval> | null = null;
  private metrics: SessionMetrics;
  // API endpoints
  private productionEndpoint = 'https://sessionable.pages.dev/api/sessions';
  private debugEndpoint = 'http://localhost:5173/api/sessions';
  private batchSize = 50;
  private batchInterval = 5000; // ms
  // Local storage keys
  private static readonly ANON_ID_STORAGE_KEY = 'sessionable_anonymous_id';

  /**
   * Creates a new Sessionable instance
   */
  constructor(options: SessionableOptions) {
    // Set default options
    this.options = {
      apiKey: options.apiKey,
      autoStart: options.autoStart || false,
      metadata: options.metadata || {},
      maskInputs: options.maskInputs || false,
      maskTextSelector: options.maskTextSelector,
      debug: options.debug || false,
      useDeviceId: options.useDeviceId !== undefined ? options.useDeviceId : true
    };
    
    // Initialize or retrieve the anonymous user ID
    this.anonymousUserId = this.getOrCreateAnonymousId();

    // Initialize metrics
    this.metrics = {
      sessionId: '',
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'disconnected',
      lastError: null,
      lastBatchSize: 0,
      lastSuccessfulUpload: null,
      recordingStartTime: new Date(),
      isRecording: false,
    };

    // Automatically start recording if option is set
    if (this.options.autoStart) {
      this.start();
    }
  }

  /**
   * Start recording the session
   */
  public start(): void {
    if (this.metrics.isRecording) return;

    // Generate a new session ID
    const sessionId = uuidv4();
    
    // Store locally if localStorage is available
    try {
      localStorage.setItem('sessionable_session_id', sessionId);
    } catch (e) {
      // Ignore localStorage errors
    }

    // Reset events array
    this.events = [];

    // Update metrics
    this.updateMetrics({
      sessionId,
      recordingStartTime: new Date(),
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'connecting',
      isRecording: true
    });

    // Start recording with rrweb
    const stop = rrweb.record({
      emit: (event) => {
        // Push event to array
        this.events.push(event);

        // Update metrics
        this.updateMetrics({
          eventsRecorded: this.metrics.eventsRecorded + 1
        });

        // Send batch if we've reached the threshold
        if (this.events.length >= this.batchSize) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      },
      // Configure rrweb options based on user settings
      inlineStylesheet: true,
      maskAllInputs: this.options.maskInputs,
      maskTextSelector: this.options.maskTextSelector,
      recordCanvas: true,
      collectFonts: true,
    });

    // Set up interval to periodically send events
    this.eventsInterval = setInterval(() => {
      if (this.events.length > 0) {
        const eventsToSend = [...this.events];
        this.sendEvents(eventsToSend);
        this.events = [];
      }
    }, this.batchInterval);

    // Save stop function for later
    this.stopFn = stop || null;
  }

  /**
   * Stop recording the session
   */
  public stop(): void {
    if (!this.metrics.isRecording || !this.stopFn) return;

    // Stop recording
    this.stopFn();
    this.stopFn = null;

    // Clear interval
    if (this.eventsInterval) {
      clearInterval(this.eventsInterval);
      this.eventsInterval = null;
    }

    // Send any remaining events
    if (this.events.length > 0) {
      const finalEvents = [...this.events];
      this.events = [];
      this.sendEvents(finalEvents);
    }

    // Update metrics
    this.updateMetrics({
      isRecording: false
    });
  }

  /**
   * Get current session metrics
   */
  public getMetrics(): SessionMetrics {
    // Make sure we create a proper copy with Date objects intact
    return {
      ...this.metrics,
      // Ensure recordingStartTime remains a Date object
      recordingStartTime: new Date(this.metrics.recordingStartTime),
      lastSuccessfulUpload: this.metrics.lastSuccessfulUpload ? 
        new Date(this.metrics.lastSuccessfulUpload) : null
    };
  }

  /**
   * Add custom metadata to the session
   */
  public addMetadata(metadata: Record<string, any>): void {
    this.options.metadata = {
      ...this.options.metadata,
      ...metadata
    };
  }

  /**
   * Send events to the Sessionable API
   */
  private async sendEvents(events: rrweb.eventWithTime[]): Promise<void> {
    if (events.length === 0) return;

    this.updateMetrics({
      lastBatchSize: events.length,
      batchesSent: this.metrics.batchesSent + 1
    });

    try {
      // Prepare metadata with basic information
      const metadata = {
        ...this.options.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventCount: events.length,
      };

      // Prepare payload according to API format
      const payload: SessionPayload = {
        session_id: this.metrics.sessionId,
        anonymous_id: this.anonymousUserId,
        events: events,
        metadata: metadata
      };

      // Determine which endpoint to use
      const apiEndpoint = this.options.debug ? this.debugEndpoint : this.productionEndpoint;
      
      if (this.options.debug) {
        console.log('[Sessionable] Debug mode enabled, using endpoint:', apiEndpoint);
      }
      
      // Send to API
      await axios.post(apiEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.options.apiKey}`,
        }
      });

      // Update metrics on success
      this.updateMetrics({
        connectionStatus: 'connected',
        batchesSuccessful: this.metrics.batchesSuccessful + 1,
        lastSuccessfulUpload: new Date(),
        lastError: null
      });
    } catch (error) {
      // Update metrics on error
      this.updateMetrics({
        connectionStatus: 'error',
        lastError: error instanceof Error ? error.message : 'Unknown error'
      });
      console.error('[Sessionable] Failed to send session data:', error);
    }
  }

  /**
   * Update session metrics
   */
  private updateMetrics(updates: Partial<SessionMetrics>): void {
    this.metrics = {
      ...this.metrics,
      ...updates
    };
  }
  
  /**
   * Get existing anonymous ID from local storage or create a new one
   * 
   * If useDeviceId is true, uses FingerprintJS to generate a stable device ID
   * and maps it to a human-readable ID.
   * 
   * If useDeviceId is false, creates human-readable IDs with a number suffix 
   * for high uniqueness. Format example: "happy-blue-cats-dance-quickly-1234"
   */
  private getOrCreateAnonymousId(): string {
    try {
      // Try to get existing anonymous ID from localStorage first
      const existingId = localStorage.getItem(Sessionable.ANON_ID_STORAGE_KEY);
      
      if (existingId) {
        if (this.options.debug) {
          console.log('[Sessionable] Using existing anonymous ID:', existingId);
        }
        return existingId;
      }
      
      // If no existing ID is found, we need to create a new one
      if (this.options.useDeviceId) {
        // Use FingerprintJS to get a device fingerprint and map it to a human-readable ID
        // We'll do this asynchronously and store a temporary ID first
        this.generateDeviceId().then(deviceBasedId => {
          if (deviceBasedId) {
            // Update the ID in storage and in the current instance
            localStorage.setItem(Sessionable.ANON_ID_STORAGE_KEY, deviceBasedId);
            this.anonymousUserId = deviceBasedId;
            
            if (this.options.debug) {
              console.log('[Sessionable] Updated to device-based ID:', deviceBasedId);
            }
          }
        }).catch(error => {
          if (this.options.debug) {
            console.error('[Sessionable] Failed to generate device ID:', error);
          }
        });
      }
      
      // Generate a temporary or fallback human-readable ID
      const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const newId = `${humanId({
        separator: '-',
        capitalize: false,
        adjectiveCount: 2,
        addAdverb: true
      })}-${randomSuffix}`;
      
      // Store in localStorage for future use (this may be replaced by the device ID later)
      localStorage.setItem(Sessionable.ANON_ID_STORAGE_KEY, newId);
      
      if (this.options.debug) {
        console.log('[Sessionable] Created new anonymous ID:', newId);
      }
      
      return newId;
    } catch (e) {
      // If localStorage is not available, generate an ID but don't persist it
      const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const fallbackId = `${humanId({
        separator: '-',
        capitalize: false,
        adjectiveCount: 2,
        addAdverb: true
      })}-${randomSuffix}`;
      
      console.warn('[Sessionable] Failed to store anonymous ID in localStorage:', e);
      return fallbackId;
    }
  }
  
  /**
   * Generate a device-specific ID using FingerprintJS
   * Maps the fingerprint hash to a human-readable ID format using the human-id package
   */
  private async generateDeviceId(): Promise<string | null> {
    try {
      // Initialize FingerprintJS
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;
      
      // Get the visitor identifier
      const result = await fp.get();
      const fingerprint = result.visitorId;
      
      if (this.options.debug) {
        console.log('[Sessionable] Generated fingerprint:', fingerprint);
      }
      
      // Extract a numeric seed from the fingerprint to ensure deterministic IDs
      // This ensures the same device always gets the same human ID
      let currentSeed = parseInt(fingerprint.substring(0, 8), 16);
      
      // Create a deterministic suffix from the fingerprint
      const numericSuffix = (currentSeed % 10000).toString().padStart(4, '0');
      
      // Use the Math.random function to seed the human-id generator
      // Store the original Math.random
      const originalRandom = Math.random;
      
      try {
        // Replace Math.random with a seeded version
        // This is a hack but it allows us to use the human-id package with a seed
        Math.random = () => {
          // Simple linear congruential generator with our seed
          const a = 1664525;
          const c = 1013904223;
          const m = Math.pow(2, 32);
          // Update seed for next call but use the same initial seed for each new ID
          // This makes the generation deterministic based on the fingerprint
          currentSeed = (a * currentSeed + c) % m;
          return currentSeed / m;
        };
        
        // Now use the human-id package with our seeded Math.random
        const deviceId = `${humanId({
          separator: '-',
          capitalize: false,
          adjectiveCount: 2,
          addAdverb: true
        })}-${numericSuffix}`;
        
        return deviceId;
      } finally {
        // Restore the original Math.random no matter what
        Math.random = originalRandom;
      }
    } catch (error) {
      console.error('[Sessionable] Error generating device ID:', error);
      return null;
    }
  }
}